import * as Api from '@ViewModels';
import { UseFormReset } from 'react-hook-form';
import { useHistory } from 'react-router';
import { toast } from 'sonner';
import { useErrorMessages } from '../../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../../models/Logging';
import {
	invalidateInfiniteSurveys,
	invalidateSurveyById,
	useCreateSatisfactionSurveyMutation,
	useReorderSatisfactionSurveyFieldsMutation,
	useUpdateSatisfactionSurveyMutation,
} from '../../../../../queries';
import { SatisfactionSurveyFormData } from '../utils';

interface UseSatisfactionSurveyMutationsProps {
	impersonationContext?: Api.IImpersonationContext;
	getValues: () => SatisfactionSurveyFormData;
	reset: UseFormReset<SatisfactionSurveyFormData>;
	surveyData?: Api.ISatisfactionSurvey;
	isClone: boolean;
	onReorderSuccess?: (fields: Api.IFormField<string>[]) => void;
}

// Hook for mutations - follows EventRegistrationSurvey and CustomSurvey patterns
export const useSatisfactionSurveyMutations = (props: UseSatisfactionSurveyMutationsProps) => {
	const { impersonationContext, getValues, reset, onReorderSuccess } = props;
	const history = useHistory();
	const { logApiError } = useEventLogging('SatisfactionSurvey');
	const errorMessages = useErrorMessages();

	// Create new survey - single API call handles everything
	const createSurveyMutation = useCreateSatisfactionSurveyMutation({
		onError: error => {
			errorMessages.pushApiError(error);
			toast.error('Failed to create satisfaction survey');
			logApiError('SatisfactionCreateSurvey-Error', error);
		},
		onSuccess: (val: Api.ISatisfactionSurvey) => {
			toast.success('Satisfaction survey created successfully');
			history.replace(`/surveys/satisfaction/edit/${val.id}`);
			invalidateInfiniteSurveys();
			invalidateSurveyById();
			// Reset form dirty state while keeping current values
			const formValues = getValues();
			reset({ ...formValues }, { keepValues: true, keepDefaultValues: false });
		},
	});

	// Update existing survey - single API call handles everything
	const updateSurveyMutation = useUpdateSatisfactionSurveyMutation({
		onError: error => {
			errorMessages.pushApiError(error);
			toast.error('Failed to update satisfaction survey');
			logApiError('SatisfactionUpdateSurvey-Error', error);
		},
		onSuccess: () => {
			toast.success('Satisfaction survey updated successfully');
			invalidateInfiniteSurveys();
			invalidateSurveyById();
			// Reset form dirty state while keeping current values
			const formValues = getValues();
			reset({ ...formValues }, { keepValues: true, keepDefaultValues: false });
		},
	});

	const reorderSurveyFieldsMutation = useReorderSatisfactionSurveyFieldsMutation({
		impersonationContext,
		onError: error => {
			errorMessages.pushApiError(error);
			toast.error('Failed to reorder satisfaction survey fields');
			logApiError('SatisfactionReorderSurveyFields-Error', error);
		},
		onSuccess: data => {
			toast.success('Satisfaction survey fields reordered successfully');
			if (onReorderSuccess && data?.fields) {
				onReorderSuccess(data.fields);
			}
		},
	});

	return {
		createSurveyMutation,
		updateSurveyMutation,
		reorderSurveyFieldsMutation,
	};
};
